import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { useNotificationStore } from '@/stores/notifications';

import { TProgram } from '../types';
import { TProgramBroadcastCandidate } from '../utils/programBroadcastCandidates';

import { useProgramBroadcastBatch } from './useProgramBroadcastBatch';

/**
 * Owns the generation modal's lifecycle: opening it on a programme, running the
 * batch, and closing it only when every emission of the batch succeeded. A
 * partial or cancelled batch keeps the modal open so the user can read what is
 * missing and generate again — the ones that did succeed will be skipped.
 */
export function useProgramBroadcastGenerationController() {
  const { t } = useTranslation();
  const { addSuccessNotification } = useNotificationStore();
  const { run, cancel, reset, isRunning, progress, result } = useProgramBroadcastBatch();
  const generationModal = useDisclosureWithData<TProgram>({ onClose: reset });

  const openGeneration = useCallback(
    (program: TProgram) => {
      reset();
      generationModal.open(program);
    },
    [generationModal.open, reset]
  );

  const handleGenerate = useCallback(
    async (candidates: TProgramBroadcastCandidate[], program: TProgram) => {
      const batchResult = await run(candidates, program);

      if (batchResult.failed.length || batchResult.isCancelled) return;

      // A fully successful batch closes the modal, and closing resets the
      // result — so the report the modal would have shown never reaches the
      // user. The batch deliberately skips the per-request toast, which leaves
      // the happy path silent unless it says so once, here.
      addSuccessNotification(
        t('Se han creado {{count}} emisiones', { count: batchResult.createdCount }),
        t('El proceso ha sido un éxito')
      );
      generationModal.close();
    },
    [run, generationModal.close, addSuccessNotification, t]
  );

  return {
    generationModal,
    openGeneration,
    handleGenerate,
    onCancelGeneration: cancel,
    // Editing the period or the slots after a partial run makes the report of
    // that run stale; the modal clears it so the two can never contradict.
    onResultStale: reset,
    isRunning,
    progress,
    result,
  };
}
